import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import logger from '../utils/logger';
import Navbar from '../components/Navbar';
import {
  Fingerprint, ShieldCheck, ScanFace, FileCheck,
  CheckCircle, ArrowRight, RefreshCw, Lock, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { driver: 'Driver', carrier_owner: 'Carrier Owner', dispatcher: 'Dispatcher' };

const FEATURES = [
  { icon: ScanFace, color: 'blue', title: 'Face Verification', desc: 'AI-powered liveness detection prevents fake accounts' },
  { icon: FileCheck, color: 'green', title: 'Document Check', desc: "Instant verification of driver's license, passport, or state ID" },
  { icon: Lock, color: 'purple', title: 'Bank-Level Security', desc: 'Enterprise-grade encryption protects your information' },
  { icon: Zap, color: 'orange', title: 'Quick Process', desc: 'Complete verification in under 2 minutes' },
];

const STEPS = [
  { step: '1', title: 'Click Verify', desc: 'Opens the Persona verification window' },
  { step: '2', title: 'Scan Your ID', desc: 'Take a photo of your government ID' },
  { step: '3', title: 'Quick Selfie', desc: 'Confirm your identity with a selfie' },
];

// --- Extracted static sub-components ---

const FeatureCard = ({ icon: Icon, color, title, desc }) => (
  <div className="flex items-start gap-3 bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
    <div className={`p-1.5 sm:p-2 rounded-lg bg-${color}-500/15 flex-shrink-0`}>
      <Icon className={`h-4 w-4 sm:h-5 sm:w-5 text-${color}-400`} />
    </div>
    <div className="min-w-0">
      <p className="text-white text-xs sm:text-sm font-medium">{title}</p>
      <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const TrustBadges = () => (
  <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-white/10">
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      <CheckCircle className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
      Trusted by 100M+ verifications
    </div>
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      <ShieldCheck className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
      SOC 2 Type II Certified
    </div>
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      <Lock className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" />
      GDPR & CCPA Compliant
    </div>
  </div>
);

const StepIndicator = ({ step, title, desc }) => (
  <div className="flex-1 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
    <div className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-cyan-500/20 text-cyan-400 text-xs sm:text-sm font-bold mb-1.5 sm:mb-2">
      {step}
    </div>
    <p className="text-white text-xs sm:text-sm font-medium">{title}</p>
    <p className="text-slate-400 text-xs mt-0.5">{desc}</p>
  </div>
);

// --- Main component ---

const Verification = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);          // { has_verification, status, ... }
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);
  const activeInquiryId = useRef(null);                 // Track current inquiry ID
  const hasRedirected = useRef(false);                  // Prevent duplicate toasts/redirects

  // --- Core: navigate to profile after approval ---
  const goToProfile = useCallback(async () => {
    if (hasRedirected.current) return;
    hasRedirected.current = true;
    const updated = await refreshUser();
    if (updated?.verification_status === 'approved') {
      toast.success('Verification approved! Welcome to StraightConnect.');
      navigate('/profile', { replace: true });
    } else {
      hasRedirected.current = false; // reset if refresh didn't confirm
    }
  }, [refreshUser, navigate]);

  // --- Core: check backend verification status ---
  const checkStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/verification/user-status');
      setStatus(data);
      if (data.status === 'approved') {
        await goToProfile();
      }
      return data;
    } catch (error) {
      logger.error('Verification status check error:', error);
      setStatus({ has_verification: false, status: 'not_started' });
      return null;
    }
  }, [goToProfile]);

  // --- Report client-side completion to backend ---
  const reportClientComplete = useCallback(async (inquiryId) => {
    if (!inquiryId) return;
    try {
      await api.post('/persona/client-complete', { inquiryId, status: 'completed' });
    } catch (error) {
      logger.error('client-complete report failed:', error);
    }
    await checkStatus();
  }, [checkStatus]);

  // --- Effect 1: Initial load — redirect if already approved, else fetch status ---
  useEffect(() => {
    if (user?.verification_status === 'approved') {
      goToProfile();
      return;
    }
    checkStatus().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Effect 2: Poll backend every 5s while verification is in progress ---
  useEffect(() => {
    if (!status?.has_verification || status?.status === 'approved') return;

    const poll = setInterval(() => checkStatus(), 5000);
    return () => clearInterval(poll);
  }, [status?.has_verification, status?.status, checkStatus]);

  // --- Effect 3: Detect mobile return via visibilitychange ---
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && activeInquiryId.current) {
        // User returned from Persona tab — report completion and recheck
        reportClientComplete(activeInquiryId.current);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reportClientComplete]);

  // --- Start Persona verification ---
  const handleStartVerification = async () => {
    setInitiating(true);
    try {
      const { data } = await api.post('/persona/create-inquiry');
      const { inquiryId, environmentId } = data;
      activeInquiryId.current = inquiryId;

      // Update local state so polling starts immediately
      setStatus({ has_verification: true, status: 'created', inquiry_id: inquiryId });

      const personaUrl = `https://inquiry.withpersona.com/verify?inquiry-id=${inquiryId}&environment-id=${environmentId}`;

      // Unified approach: try popup first, fall back to new tab for all browsers
      const w = 700, h = 750;
      const left = (window.screen.availWidth - w) / 2;
      const top = (window.screen.availHeight - h) / 2;
      const popup = window.open(personaUrl, 'persona-verification',
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no`
      );

      if (!popup || popup.closed) {
        window.open(personaUrl, '_blank');
        toast.info('Verification opened in a new tab. Complete it there and return here.', { duration: 8000 });
      } else {
        toast.info('Complete your verification in the popup window. Your status will update automatically.');
        const pollPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollPopup);
            reportClientComplete(inquiryId);
            let rapidCount = 0;
            const rapidPoll = setInterval(async () => {
              rapidCount++;
              const result = await checkStatus();
              if (result?.status === 'approved' || rapidCount >= 12) {
                clearInterval(rapidPoll);
              }
            }, 2500);
          }
        }, 1500);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start verification. Please try again.';
      logger.error('Failed to start verification:', error);
      toast.error(msg, { duration: 6000 });
    } finally {
      setInitiating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const roleLabel = ROLE_LABELS[user?.role] || 'Member';
  const hasStarted = status?.has_verification && status?.status !== 'not_started';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-y-auto" data-testid="verification-page">
      <Navbar verificationMode={true} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-16 pb-12 sm:pb-20">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-6 sm:mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 mb-4 sm:mb-6 shadow-lg shadow-blue-500/25">
            <Fingerprint className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">Verify Your Identity</h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-lg mx-auto px-2">
            Welcome, <span className="text-white font-medium">{user?.name}</span>. Complete identity verification powered by{' '}
            <span className="text-cyan-400 font-semibold">Persona</span> to unlock your {roleLabel} profile.
          </p>
        </motion.div>

        {/* Info Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-8 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-white">What is Persona Verification?</h2>
          </div>
          <p className="text-slate-300 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
            Persona is a trusted identity verification platform used by leading companies worldwide. It uses advanced AI to verify government-issued IDs and perform liveness checks — ensuring every member on StraightConnect is a real, verified person. The process takes under two minutes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {FEATURES.map((f) => <FeatureCard key={f.title} {...f} />)}
          </div>
          <TrustBadges />
        </motion.div>

        {/* CTA Button */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="space-y-3 sm:space-y-4">
          <button
            onClick={handleStartVerification}
            disabled={initiating}
            className="w-full py-3.5 sm:py-4 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 text-sm sm:text-base"
            data-testid="start-persona-verification-btn"
          >
            {initiating ? (
              <><RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" /> Starting Verification...</>
            ) : hasStarted ? (
              <><Fingerprint className="h-4 w-4 sm:h-5 sm:w-5" /> Continue Verification <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" /></>
            ) : (
              <><Fingerprint className="h-4 w-4 sm:h-5 sm:w-5" /> Start Persona Verification <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" /></>
            )}
          </button>

          {hasStarted && (
            <div className="text-center">
              <button onClick={checkStatus} className="text-xs sm:text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 mx-auto" data-testid="refresh-status-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh verification status
              </button>
            </div>
          )}
        </motion.div>

        {/* How it works */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.45 }} className="mt-8 sm:mt-10 pb-6">
          <h3 className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider mb-3 sm:mb-4 text-center">How it works</h3>
          <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4">
            {STEPS.map((s) => <StepIndicator key={s.step} {...s} />)}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Verification;
